/**
 * JWT token utilities for self-registration
 * Decodes and validates JWT tokens without external dependencies
 */

export interface DecodedJwt {
    header: Record<string, unknown>;
    payload: Record<string, unknown>;
    signature: string;
}

export interface ExtractedUserData {
    id?: number | string;
    email?: string;
    registrationId?: number | string;
    [key: string]: unknown;
}

/**
 * Decode JWT token into header, payload, and signature
 * Does NOT validate signature - use only with tokens from trusted sources (admin portal)
 */
export const decodeJwt = (token: string): DecodedJwt | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        const [headerB64, payloadB64] = parts;

        // Decode base64url to base64
        const addPadding = (str: string) => {
            const padding = 4 - (str.length % 4);
            if (padding !== 4) {
                return str + '='.repeat(padding);
            }
            return str;
        };

        try {
            const header = JSON.parse(atob(addPadding(headerB64)));
            const payload = JSON.parse(atob(addPadding(payloadB64)));

            return {
                header,
                payload,
                signature: parts[2],
            };
        } catch {
            return null;
        }
    } catch {
        return null;
    }
};

/**
 * Check if JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
    const decoded = decodeJwt(token);
    const exp = decoded?.payload.exp;
    if (typeof exp !== 'number') {
        return false; // If no expiration, consider it valid
    }

    const now = Math.floor(Date.now() / 1000);
    return exp < now;
};

/**
 * Extract user-relevant data from JWT token payload
 * Looks for common user data fields that might be in the token
 */
export const extractUserData = (token: string): ExtractedUserData => {
    const decoded = decodeJwt(token);
    if (!decoded) {
        return {};
    }

    const payload = decoded.payload;
    const userData: ExtractedUserData = {};

    // Extract common user data fields
    const idOrSub = payload.id || payload.sub;
    if ((typeof idOrSub === 'number' || typeof idOrSub === 'string') && idOrSub) {
        userData.id = idOrSub;
    }
    if (typeof payload.email === 'string' && payload.email) {
        userData.email = payload.email;
    }
    if (
        (typeof payload.registrationId === 'number' || typeof payload.registrationId === 'string') &&
        payload.registrationId
    ) {
        userData.registrationId = payload.registrationId;
    }
    if ((typeof payload.user_id === 'number' || typeof payload.user_id === 'string') && payload.user_id) {
        userData.id = payload.user_id;
    }

    return userData;
};

/**
 * Validate JWT token structure and content
 */
export const validateJwtToken = (token: string): { isValid: boolean; error?: string } => {
    if (!token || !token.trim()) {
        return { isValid: false, error: 'Token is missing' };
    }

    const decoded = decodeJwt(token);
    if (!decoded) {
        return { isValid: false, error: 'Invalid token format' };
    }

    if (isTokenExpired(token)) {
        return { isValid: false, error: 'Token has expired' };
    }

    return { isValid: true };
};
